// React
import React from 'react';
import { Link } from 'react-router-dom';

// Components
import HeroSection from '@/components/blocks/HeroSection/HeroSection';
import Text from '@/components/molecules/Text/Text';
import SocialLinks from '@/components/molecules/SocialLinks/SocialLinks';
import Button from '@/components/atoms/Button/Button';
import Blob from '@/components/atoms/Blob/Blob';
import StrapiMedia from '@/components/atoms/StrapiMedia/StrapiMedia';
import ContactInteractiveForm from '@/components/organisms/ContactInteractiveForm/ContactInteractiveForm';
import RevealFadeIn from '@/components/reveal-animations/RevealFadeIn/RevealFadeIn';
import RevealScaleIn from '@/components/reveal-animations/RevealScaleIn/RevealScaleIn';

// Types
import { ContactType } from '@/views/Contact/ContactType';
import { FooterType } from '@/components/organisms/Footer/FooterType';
import { ColorThemeEnum } from '@/enum/ColorThemeEnum';
import { WaveShapeEnum } from '@/enum/WaveShapeEnum';
import { BubbleShapeEnum } from '@/enum/BubbleShapeEnum';
import { BubbleModeEnum } from '@/enum/BubbleModeEnum';
import type { Media } from '@/types/MediaType';

// Styles
import styles from './TemplateContact.module.css';

interface TemplateContactProps {
  title: ContactType['title'];
  paragraph: ContactType['paragraph'];
  expertises: ContactType['expertises'];
  origins: ContactType['origins'];
  phone: FooterType['phone'];
  mail: FooterType['mail'];
  addresses: FooterType['addresses'];
  socialMedia: FooterType['socialMedia'];
  picture?: Media | null;
  children?: React.ReactNode | null;
}

function TemplateContact({
  title,
  paragraph,
  expertises,
  origins,
  phone,
  mail,
  addresses,
  socialMedia,
  picture = null,
  children = null,
}: TemplateContactProps) {
  return (
    <>
      <HeroSection
        title={title}
        paragraph={paragraph}
        color={ColorThemeEnum.ORANGE}
        scale="large"
        shape={WaveShapeEnum.CONVEX}
        cta={null}
        sideCTA={null}
        gradient={null}
        media={null}
        className={styles.hero}
        customContent={
          <div className={styles.heroContent}>
            <div className={styles.heroGrid}>
              <div className={styles.infos}>
                <Text title={title} paragraph={paragraph} cta={null} className={styles.heroText} />

                <RevealFadeIn>
                  <div className={styles.cta}>
                    {phone ? (
                      <Link to={`tel:${phone}`}>
                        <Button size="small" color={ColorThemeEnum.YELLOW}>{phone}</Button>
                      </Link>
                    ) : null}
                    {mail ? (
                      <Link to={`mailto:${mail}`}>
                        <Button size="small" color={ColorThemeEnum.YELLOW}>{mail}</Button>
                      </Link>
                    ) : null}
                  </div>
                </RevealFadeIn>

                {addresses && addresses.length > 0 ? (
                  <RevealFadeIn>
                    <div className={styles.addresses}>
                      {addresses.map((address) => (
                        <div key={`${address.city}-${address.street}`} className={styles.addressItem}>
                          {address.city ? <p className={styles.addressCity}>{address.city}</p> : null}
                          {address.street ? <p>{address.street}</p> : null}
                          {address.detail ? <p>{address.detail}</p> : null}
                        </div>
                      ))}
                    </div>
                  </RevealFadeIn>
                ) : null}

                {socialMedia && socialMedia.length > 0 ? (
                  <RevealFadeIn>
                    <SocialLinks color="white" links={socialMedia} />
                  </RevealFadeIn>
                ) : null}
              </div>

              <RevealScaleIn className={styles.mediaColumn}>
                <Blob
                  theme={null}
                  shape={BubbleShapeEnum.SHAPE_4}
                  color={ColorThemeEnum.WHITE}
                  mode={BubbleModeEnum.WRAPPED}
                  className={styles.mediaBlob}
                >
                  {picture?.url ? <StrapiMedia image={picture} format="xlarge" className={styles.mediaImage} /> : null}
                </Blob>
              </RevealScaleIn>
            </div>

            <ContactInteractiveForm
              className={styles.interactiveForm}
              expertises={expertises}
              origins={origins}
            />
          </div>
        }
      />
      {children}
    </>
  );
}

export default TemplateContact;
